import {useEffect, useMemo, useRef, useState} from 'react'
import {Pressable, ScrollView, View} from 'react-native'
import {
  ImageBridge as DefaultImageBridge,
  LinkBridge as DefaultLinkBridge,
  RichText,
  TenTapStartKit,
  UnderlineBridge as DefaultUnderlineBridge,
  useBridgeState,
  useEditorBridge,
} from '@10play/tentap-editor'
import {type AtUriString} from '@atproto/syntax'
import {msg} from '@lingui/core/macro'
import {useLingui} from '@lingui/react'
import {Trans} from '@lingui/react/macro'
import {useNavigation} from '@react-navigation/native'
import {useQueryClient} from '@tanstack/react-query'

import {
  blobCid,
  MAX_BODY_IMAGE_BYTES,
  MAX_BODY_IMAGES,
} from '#/lib/api/article-assets'
import {resolveArticleShareLink} from '#/lib/api/article-share'
import {type ArticleEditRef, publishArticle} from '#/lib/api/articles'
import {uploadBlob} from '#/lib/api/upload-blob'
import {useOpenComposer} from '#/lib/hooks/useOpenComposer'
import {useOpenLink} from '#/lib/hooks/useOpenLink'
import {useRequireStrikerForArticleAuthoring} from '#/lib/hooks/useRequireStrikerForArticleAuthoring'
import {openPicker} from '#/lib/media/picker'
import {type PickerImage} from '#/lib/media/picker.shared'
import {
  type CommonNavigatorParams,
  type NativeStackScreenProps,
  type NavigationProp,
} from '#/lib/routes/types'
import {cleanError} from '#/lib/strings/errors'
import {displayFileName} from '#/lib/strings/filename'
import {niceDate} from '#/lib/strings/time'
import {logger} from '#/logger'
import {
  DOCUMENT_RQKEY,
  type LoadedArticleDocument,
  PUBLIC_ARTICLE_RQKEY,
  RQKEY,
  useArticleDocumentQuery,
} from '#/state/queries/articles'
import {useProfileQuery} from '#/state/queries/profile'
import {usePdsClient, useSession} from '#/state/session'
import {ArticleView} from '#/screens/Article/ArticleView'
import {atoms as a, useBreakpoints, useTheme, web} from '#/alf'
import {Button, ButtonText} from '#/components/Button'
import {Attachment01 as AttachmentIcon} from '#/components/icons/Attachment01'
import {TimesLarge_Stroke2_Corner0_Rounded as XIcon} from '#/components/icons/Times'
import {Trash_Stroke2_Corner0_Rounded as TrashIcon} from '#/components/icons/Trash'
import {Loader} from '#/components/Loader'
import * as Menu from '#/components/Menu'
import {Portal} from '#/components/Portal'
import * as Prompt from '#/components/Prompt'
import * as Toast from '#/components/Toast'
import {Text} from '#/components/Typography'
import {IS_WEB} from '#/env'
import {type com, type site} from '#/lexicons'
import {ContentBridge} from './bridges/content'
import {HonorificBridge} from './bridges/honorific'
import {
  ImageUploadBridge,
  type MenuRequest,
  subscribeToImageBlockEvents,
} from './bridges/imageUpload'
import {LinkBridge} from './bridges/link'
import {ParagraphStyleBridge} from './bridges/paragraphStyle'
import {TextAlignBridge} from './bridges/textAlign'
import {
  type ActiveField,
  subscribeToTitleSubtitleEvents,
  TitleSubtitleBridge,
} from './bridges/titleSubtitle'
import {TypographyBridge} from './bridges/typography'
import {UnderlineBridge} from './bridges/underline'
import {isAllowedColorValue} from './colorAllowlist'
import {DraftsButton} from './drafts/DraftsButton'
import {draftBodyImages, draftFacetsToEditorFacets} from './drafts/state/api'
import {
  useCleanupPublishedArticleDraftMutation,
  useSaveArticleDraftMutation,
} from './drafts/state/queries'
import {type ArticleDraftSummary} from './drafts/state/schema'
// Generated by `pnpm editor:build` - gitignored, run the build before this
// file will resolve (mirrors `TipTapSpike.tsx`'s own identical import).
import {editorHtml} from './editor-web/build/editorHtml'
import {manager} from './editor-web/manager'
import {applyFacetsToParsedDoc} from './editor-web/serializer'
import {measureImageBytes, prepareCoverImage} from './imageSize'
import {
  computeBottomToolbarLift,
  computeTopToolbarTop,
  computeWebViewMarginTop,
} from './keyboardLayout'
import {
  articleDocumentToComposeState,
  type EditingArticleState,
} from './loadedArticle'
import {Metadata, type MetadataValue} from './Metadata'
import {publishErrorMessage} from './publishError'
import {type EditorFacet, type ParagraphStyleId} from './state'
import {Toolbar} from './Toolbar'

/**
 * A `FocusArgs` value for "(re-)focus the editor without moving the
 * cursor," used in place of the more obvious `null`/no-argument form
 * everywhere this file needs that behavior.
 *
 * `null` looks like the right value for this - it's TipTap's own
 * documented default (`editor.commands.focus()`) - but `@tiptap/core`'s
 * `focus` command (`commands/focus.ts`) has a real, upstream footgun here:
 * `if ((view.hasFocus() && position === null) || position === false)
 * return true` short-circuits to a total no-op whenever the view *already*
 * reports having DOM focus, on the assumption that "already focused" means
 * "a caret is already visible." That assumption doesn't hold after a
 * cross-iframe click: the browser can hand DOM focus to the WebView's
 * `.ProseMirror` element without ever placing a visible Selection in it, so
 * `view.hasFocus()` is `true` while there's still no caret on screen - and
 * `.focus(null)` then does nothing at all. This isn't unique to this app:
 * TipTap's own community has hit the same "focus() does nothing" shape
 * repeatedly (e.g. `scrumpy/tiptap#389`) and the established workaround is
 * always the same - pass an explicit position instead of relying on the
 * bare/`null` form, since only the literal `position === null` trips the
 * guard.
 *
 * `0` is that explicit position, chosen instead of the community's more
 * common `'end'`/`'start'` specifically to avoid moving the cursor:
 * `resolveFocusPosition` (`helpers/resolveFocusPosition.ts`) treats *any*
 * falsy `position` - not just `null` - as "resolve to nothing," which
 * `focus.ts` then falls back to `editor.state.selection`, i.e. wherever the
 * selection already is. `0` gets that same "keep the current selection"
 * fallback while not being the literal value the guard checks for, so the
 * rest of the command runs for real: it leaves the selection untouched
 * (already "the same," so `tr.setSelection` is skipped) but unconditionally
 * calls `delayedFocus()`, which calls `view.focus()` a tick later via
 * `requestAnimationFrame` - the part that actually shows the caret.
 */
const REFOCUS_WITHOUT_MOVING_CURSOR = 0

/**
 * `TenTapStartKit` minus its own default `UnderlineBridge`, plus this app's
 * own bridges - must mirror `AdvancedEditor.tsx`'s web `bridges` array
 * exactly by `.name` (confirmed via `RichText/utils.ts`:
 * `window.bridgeExtensionConfigMap` is built by reducing over *this* native
 * array, keyed by name - a bridge present on one side and missing on the
 * other silently loses its config entry, not a loud error). Bare
 * `@10play/tentap-editor` import is correct here, unlike the web bundle -
 * this is native RN code (Metro, real `react-native-webview` available),
 * the same import `TipTapSpike.tsx` already uses successfully; the `/web`
 * packaging bug and the `RichText`/`react-native-webview` Jest crash both
 * only ever affected the Vite web bundle and Jest respectively, never real
 * native code.
 */
const nativeBridgeExtensions = [
  ...TenTapStartKit.filter(
    bridge =>
      bridge !== DefaultUnderlineBridge &&
      bridge !== DefaultLinkBridge &&
      // Must mirror the web side's filter exactly - see AdvancedEditor.tsx
      // for why the default ImageBridge crashes any image node, and this
      // array's own doc comment for why a bridge present on one side and
      // missing on the other silently loses its config entry.
      bridge !== DefaultImageBridge,
  ),
  UnderlineBridge,
  LinkBridge,
  TextAlignBridge,
  TypographyBridge,
  HonorificBridge,
  ImageUploadBridge,
  ParagraphStyleBridge,
  ContentBridge,
  TitleSubtitleBridge,
]

/**
 * The article-authoring screen (`articles client ui plan.md` Phase 2). New
 * screen, not a composer extension - `Composer.tsx`'s reducer is shaped
 * entirely around post/thread/quote/reply semantics that don't apply here.
 * Desktop renders as a centered modal (matching the confirmed Figma design,
 * `58:4961`); mobile renders full-screen with an accordion metadata card
 * (`58:5153`/`58:5222`) - both share this same state and logic, only the
 * outer chrome differs.
 *
 * Self-contained - only needs an `onClose` callback. Registered as a plain
 * `Stack.Screen` (`ArticleComposeScreen` below adapts the two), mirroring
 * `StarterPackWizard`/`Wizard` rather than Composer's global-overlay
 * architecture - this component already draws its own full-bleed backdrop,
 * so it needs no extra screen-level chrome (`articles client ui plan.md`
 * Phase 3).
 */
export function ArticleCompose({
  onClose,
  initialArticle,
}: {
  onClose: () => void
  initialArticle?: LoadedArticleDocument
}) {
  const {_, i18n} = useLingui()
  const t = useTheme()
  const {gtMobile} = useBreakpoints()
  const pdsClient = usePdsClient()
  const {currentAccount} = useSession()
  const queryClient = useQueryClient()
  const requireStriker = useRequireStrikerForArticleAuthoring()

  const [initial] = useState(() =>
    initialArticle
      ? articleDocumentToComposeState(initialArticle, {
          did: pdsClient.assertDid,
        })
      : undefined,
  )
  // `title`/`subtitle` are a live-mirrored *hint*, not the authoritative
  // value for any consequential decision - see `bridges/titleSubtitle.ts`'s
  // own doc comment for the full "mirror for hints, pull for decisions"
  // reasoning. `doSaveDraft`/`doPublish`/`onPressCancel`/`DraftsButton`'s
  // save-prompt check all call `editorBridge.getTitleAndSubtitle()` for a
  // fresh pull instead of reading these two directly.
  const [title, setTitle] = useState(initial?.title ?? '')
  const [subtitle, setSubtitle] = useState(initial?.subtitle ?? '')
  // Which of the three focusable fields inside the WebView currently has
  // focus - drives the formatting toolbar's disabled state below. Starts at
  // `'body'` since that is where a fresh composer's caret would land, and
  // because the WebView's own focus reporting is affirmative-only (no
  // `blur` handler - see `titleSubtitle.ts`), so it is also the correct
  // fallback for "nothing has reported focus yet".
  const [activeField, setActiveField] = useState<ActiveField>('body')
  useEffect(() => {
    return subscribeToTitleSubtitleEvents({
      change: value => {
        setTitle(value.title)
        setSubtitle(value.subtitle)
      },
      activeField: setActiveField,
    })
  }, [])
  /*
   * Guards against a real race, not a hypothetical one - confirmed by
   * tracing the actual call chain, not assumed. `loadMarkdownAndFacets`
   * (see `bridges/content.ts`) is `sendBridgeMessage`, a fire-and-forget
   * `postMessage` into the WebView's iframe - it returns immediately, well
   * before the iframe has done anything. Inside the iframe,
   * `ContentActionType.LoadMarkdownAndFacets` calls
   * `editor.commands.setContent(doc)`, which dispatches exactly one real
   * ProseMirror transaction with `docChanged: true` whenever the new
   * content differs from whatever was there before - which then fires
   * TipTap's own `onUpdate` *inside the iframe*, which sends a
   * `ContentUpdate` message *back* to this side, which is what actually
   * calls the `onChange` below. All of that happens asynchronously, after
   * `onClearComposer`/`onSelectDraft` have already returned - so a
   * synchronous `setBodyTouched(false)` right after calling
   * `loadMarkdownAndFacets` there gets silently overwritten back to `true`
   * moments later by this echo, since nothing distinguishes "the body
   * changed because the user typed" from "the body changed because we
   * just replaced its content wholesale, programmatically." Confirmed live
   * as the cause of a real reported bug: a freshly cleared ("New article")
   * or freshly reopened draft showed "Save and close" instead of "Close"
   * immediately, with nothing yet typed.
   *
   * Fix: arm this immediately before either programmatic load, and have
   * `onChange` treat exactly the next call as that load's own echo rather
   * than a genuine edit. Confirmed exactly one `onChange` fires per
   * `loadMarkdownAndFacets` call (one `setContent` call is one
   * transaction), so a single-shot flag is sufficient - not a debounce or
   * a count, since there's nothing to count.
   */
  const suppressNextBodyChangeRef = useRef(false)
  /*
   * The one place `loadMarkdownAndFacets` should ever be called from -
   * arms the suppression above immediately before sending it, so both stay
   * in sync by construction rather than by remembering to pair them at
   * every call site.
   *
   * The timeout is a safety net, not the primary mechanism: if the new
   * content happens to be identical to what's already loaded (discarding
   * an already-empty draft, for instance), `setContent` may produce no
   * `docChanged` transaction at all, so `onChange`'s own echo - the thing
   * that normally consumes this flag - never arrives. Left permanently
   * armed, it would silently swallow the *next genuine* edit's
   * dirty-tracking instead of just this programmatic one. Bounded
   * generously (comfortably longer than a same-machine `postMessage` round
   * trip ever needs) rather than tied to any exact duration - a no-op in
   * the normal case, since the real echo already clears the flag well
   * before this fires.
   */
  const loadBodyContentSilently = (markdown: string, facets: EditorFacet[]) => {
    suppressNextBodyChangeRef.current = true
    editorBridge.loadMarkdownAndFacets(markdown, facets)
    setTimeout(() => {
      suppressNextBodyChangeRef.current = false
    }, 1000)
  }
  // `initialContent` is read once, at construction - later loads (draft
  // switching, clearing) go through `editorBridge.loadMarkdownAndFacets`/
  // `setTitleAndSubtitle` instead, never by changing this option after mount
  // (TenTap doesn't react to it). Title/Sub-title's four labels are resolved
  // through Lingui here, not inside the WebView - `editor-web/` is a static
  // bundle with no i18n runtime of its own, so it only ever renders whatever
  // already-translated strings arrive through this object (see
  // `bridges/titleSubtitle.ts`'s own doc comment for the locale-change
  // caveat that comes with that).
  const editorBridge = useEditorBridge({
    customSource: editorHtml,
    bridgeExtensions: nativeBridgeExtensions,
    autofocus: false,
    avoidIosKeyboard: true,
    initialContent: {
      markdown: initial?.editor?.markdown ?? '',
      facets: initial?.editor?.facets ?? [],
      title: initial?.title ?? '',
      subtitle: initial?.subtitle ?? '',
      titleLabel: _(msg`Title`),
      titlePlaceholder: _(msg`Article title...`),
      subtitleLabel: _(msg`Sub-title`),
      subtitlePlaceholder: _(msg`Sub-title (optional)...`),
      // `editor-web/index.html` has no access to ALF (a genuinely separate
      // Vite bundle) and so cannot call `useTheme()` itself - these are
      // already-resolved values for whichever of light/dark/dim is
      // currently active, applied as CSS custom properties once the bundle
      // mounts (`AdvancedEditor.tsx`). Read once, same as every other field
      // here - a theme change while the composer is already open won't
      // re-apply, matching this object's own existing limitation.
      colors: {
        bg: t.atoms.bg.backgroundColor,
        text: t.atoms.text.color,
        textMuted: t.atoms.text_contrast_low.color,
        textSecondary: t.atoms.text_contrast_medium.color,
        textStrong: t.atoms.text_contrast_high.color,
        border: t.atoms.border_contrast_medium.borderColor,
        error: t.palette.negative_700,
        accent: t.palette.primary_500,
      },
    },
    onChange: () => {
      if (suppressNextBodyChangeRef.current) {
        suppressNextBodyChangeRef.current = false
        return
      }
      setBodyTouched(true)
    },
  })
  const bridgeState = useBridgeState(editorBridge)
  // Approximates the old exact-content-equality `isDirty` check
  // (`JSON.stringify` of the full markdown string against a baseline
  // snapshot) with a simpler touched flag - the markdown itself is no
  // longer synchronously available (it lives inside the WebView, only
  // readable via `editorBridge.getMarkdownAndFacets()`, an async round
  // trip). Real, minor precision trade-off: typing something and then
  // undoing back to the exact original text now still reads as dirty,
  // where the old snapshot comparison would have caught the match. Reset
  // explicitly wherever the body's content is replaced wholesale
  // (`onClearComposer`/`onSelectDraft`) or the draft is freshly saved.
  const [bodyTouched, setBodyTouched] = useState(false)
  const [metadata, setMetadata] = useState<MetadataValue>(
    initial?.metadata ?? {
      authors: [],
      translators: [],
      categories: [],
      tags: [],
    },
  )
  const [coverImage, setCoverImage] = useState<
    site.standard.document.Main['coverImage']
  >(initial?.coverImage)
  const [coverImagePreviewUri, setCoverImagePreviewUri] = useState<
    string | undefined
  >(initial?.coverImagePreviewUri)
  /**
   * Blob refs for images embedded in the body, with the filename each was
   * picked under.
   *
   * Held here and persisted onto the draft rather than being derivable on
   * demand: the markdown only carries each image's CID, and a `BlobRef` needs
   * `mimeType` and `size` too, neither of which can be read back for a blob
   * that was never tethered. This list is the only place those live until the
   * article is published.
   */
  const [bodyImages, setBodyImages] = useState<
    com.sunnahsky.article.draft.defs.BodyImage[]
  >(initial?.bodyImages ?? [])
  /**
   * The editor surface, measured so the image menu can be anchored correctly.
   *
   * The rect an image block reports comes from `getBoundingClientRect()`
   * *inside the WebView*, so it is relative to that document's own viewport -
   * it knows nothing about where the editor sits on the page. Anchoring
   * straight to it put the menu near the top-left of the screen instead of on
   * the block. Adding this surface's own page position converts one coordinate
   * space to the other.
   */
  const editorSurfaceRef = useRef<React.ComponentRef<typeof View>>(null)
  /**
   * The block whose edit menu is open, and where to anchor it.
   *
   * Discriminated by `kind` (see `bridges/imageUpload.ts`'s `MenuRequest`) so
   * the one menu below can render either an image's Remove/Replace pair or a
   * placeholder's Select image/Delete block pair, depending on which kind of
   * block the author clicked.
   */
  const [imageMenuTarget, setImageMenuTarget] = useState<
    MenuRequest | undefined
  >(undefined)
  const imageMenuControl = Menu.useMenuControl()
  /*
   * Same override as the cover-image menu in `Metadata.tsx`: the design puts
   * these glyphs at `border_contrast_high` rather than `Menu.ItemIcon`'s own
   * `text_contrast_medium` default. Referenced through the semantic atom so it
   * follows the theme, since ALF inverts the palette for dark and dim.
   */
  const imageMenuIconFill = () => t.atoms.border_contrast_high.borderColor
  const [flavor] = useState<'gfm' | 'commonmark'>(initial?.flavor ?? 'gfm')
  const [isPublishing, setIsPublishing] = useState(false)
  /**
   * The mandatory pre-publish preview's frozen snapshot - set once validation
   * passes, cleared by "Back to edit". Not persisted anywhere: this is a
   * read-only review of data that already lives in the editor/local state,
   * not a second source of truth for it.
   */
  const [previewSnapshot, setPreviewSnapshot] = useState<
    | {title: string; subtitle: string; markdown: string; facets: EditorFacet[]}
    | undefined
  >(undefined)
  /**
   * Whether entering preview pushed a synthetic browser-history entry that's
   * still sitting on top of the stack - see the `popstate` effect below for
   * the full account of why this exists. `false` whenever nothing of ours
   * is currently on top: before preview is ever opened, and again as soon
   * as either exit path (the physical back button or the "Back to edit"
   * link) has consumed the entry it pushed.
   */
  const previewHistoryPushedRef = useRef(false)
  /**
   * CID -> local file URI, session-only and deliberately kept off
   * `bodyImages` itself - `bodyImages` is serialized straight into the
   * persisted draft record (`articleStateToDraft`), and this project already
   * confirmed an unrecognized field on that record survives `validate: true`
   * unstripped, not rejected. A real on-device path here would leak into the
   * stored draft silently. Lets the preview show a real thumbnail before the
   * blob is tethered, exactly like the cover image's own local preview
   * already does - starts empty on a reopened draft, same session as any
   * other in-memory-only state.
   */
  const [localImageUris, setLocalImageUris] = useState<Record<string, string>>(
    {},
  )
  const [draftId, setDraftId] = useState<string | undefined>(undefined)
  const [draftUpdatedAt, setDraftUpdatedAt] = useState<string | undefined>(
    undefined,
  )
  const [editingArticle, setEditingArticle] = useState<
    EditingArticleState | undefined
  >(initial?.editingArticle)

  // Baseline-snapshot dirty tracking, not a reducer's per-action `isDirty`
  // flag like the post Composer's - there's no reducer here (plain
  // `useState` fields), and sprinkling `setIsDirty(true)` across every
  // handler is easy to miss one. Reset on mount, after a successful
  // save-to-draft, and after loading a selected draft. Covers
  // title/subtitle/metadata/coverImage only - the body's own dirtiness is
  // `bodyTouched` above, tracked separately since the markdown itself
  // isn't synchronously available to snapshot the way it used to be.
  //
  // `snapshotOf` takes explicit values rather than closing over
  // title/subtitle/metadata/coverImage directly, because the two handlers
  // below (`onClearComposer`/`onSelectDraft`) need to compute the new
  // baseline in the same synchronous call that calls `setTitle` etc. -
  // `setState` doesn't flush before the next line runs, so reading the
  // component's own state variables right after setting them would capture
  // the stale pre-update values, not what was just set.
  const snapshotOf = (v: {
    title: string
    subtitle: string
    metadata: MetadataValue
    coverImage: site.standard.document.Main['coverImage']
  }) => JSON.stringify(v)
  const snapshot = () => snapshotOf({title, subtitle, metadata, coverImage})
  const baselineRef = useRef(snapshot())
  const isDirty = bodyTouched || snapshot() !== baselineRef.current

  const hasContent =
    !!title.trim() ||
    !!subtitle.trim() ||
    !bridgeState.empty ||
    !!coverImage ||
    metadata.authors.length > 0 ||
    metadata.translators.length > 0 ||
    metadata.categories.length > 0 ||
    metadata.tags.length > 0

  /**
   * The same `hasContent`/`isDirty` formulas above, parameterized on a fresh
   * title/subtitle pair instead of the live-mirrored state ones. Every
   * one-shot decision that would otherwise discard content on a wrong answer
   * - Cancel's discard prompt, the Drafts button's save prompt - calls
   * `editorBridge.getTitleAndSubtitle()` first and recomputes through this,
   * rather than trusting `hasContent`/`isDirty` directly. Those two stay
   * exactly as they are for continuous display purposes (they're accurate
   * within ~100ms, which is fine for a UI hint); they're just never treated
   * as authoritative for a decision that would lose data if wrong. See
   * `bridges/titleSubtitle.ts`'s own doc comment for the full reasoning.
   */
  const freshHasContentAndIsDirty = (fresh: {
    title: string
    subtitle: string
  }) => ({
    hasContent:
      !!fresh.title.trim() ||
      !!fresh.subtitle.trim() ||
      !bridgeState.empty ||
      !!coverImage ||
      metadata.authors.length > 0 ||
      metadata.translators.length > 0 ||
      metadata.categories.length > 0 ||
      metadata.tags.length > 0,
    isDirty:
      bodyTouched ||
      snapshotOf({
        title: fresh.title,
        subtitle: fresh.subtitle,
        metadata,
        coverImage,
      }) !== baselineRef.current,
  })

  const {mutateAsync: saveArticleDraft} = useSaveArticleDraftMutation()
  const {mutate: cleanupPublishedDraft} =
    useCleanupPublishedArticleDraftMutation()
  const discardPromptControl = Prompt.usePromptControl()
  const discardEditPromptControl = Prompt.usePromptControl()
  const shareArticlePromptControl = Prompt.usePromptControl()
  const {openComposer} = useOpenComposer()
  // Set only on a fresh publish (never an edit - see the share-prompt
  // decision in `encapsulated-squishing-thacker.md`). Also carries the
  // just-published article's identity/title forward for the prompt's own
  // `openComposer` call, since `previewSnapshot` is cleared by the time it
  // fires.
  const [justPublished, setJustPublished] = useState<
    {documentUri: AtUriString; title: string} | undefined
  >()

  /*
   * Closes a real gap, web only: this screen's Cancel button already gates
   * on unsaved changes (`onPressCancel` below), but the browser's own back
   * button bypasses it entirely, going straight through React Navigation's
   * web-linking integration with no warning at all. Reported specifically
   * from preview mode, but the gap was never preview-specific - preview
   * renders the same underlying editor state, just with different JSX on
   * top of it (Piece 4's own design: leaving preview is lossless, the
   * editor's state is never touched), so one guard here covers both editor
   * and preview mode for free.
   *
   * A first pass here tried to intercept the removal (`usePreventRemove`,
   * the same primitive `FindContactsFlowScreen.tsx` already uses) and
   * route it through this screen's own Save/Discard prompts, dispatching
   * the original navigation action on confirm. Deliberately reverted, at
   * the owner's own direction: that's real, self-imposed complexity this
   * screen doesn't need - the platform already has a purpose-built
   * mechanism for exactly this, and a generic browser warning is more
   * honest about what's actually happening (a page navigation the app
   * cannot cleanly redirect into a custom flow) than dressing it up as an
   * in-app decision. `CreateOrEditListDialog.tsx` already uses this same
   * `beforeunload` pattern for an unrelated dirty-state warning - matched
   * here rather than reinvented. If the user proceeds anyway, the changes
   * are genuinely lost, on their own informed call; if they want them
   * back, they use the browser's own forward button and land back in
   * preview or the editor exactly as they left it, no special handling
   * needed since neither actually unmounted anything before this point.
   *
   * Gated on the same mirrored `hasContent`/`isDirty` values `closeLabel`
   * and `onPressCancel` already read, not a fresh pull - a `beforeunload`
   * handler has no opportunity to await anything before the browser needs
   * an answer, so "accurate within ~100ms" is the correct tool here, not a
   * limitation to work around.
   */
  useEffect(() => {
    if (!IS_WEB) return
    const shouldWarn = editingArticle
      ? isDirty
      : hasContent && (!draftId || isDirty)
    if (!shouldWarn) return
    const abortController = new AbortController()
    window.addEventListener('beforeunload', event => event.preventDefault(), {
      signal: abortController.signal,
    })
    return () => abortController.abort()
  }, [editingArticle, isDirty, hasContent, draftId])

  /*
   * Makes the browser's physical back button, while the mandatory
   * pre-publish preview is open, behave exactly like the visible "Back to
   * edit" link - not a discard-changes warning, since there is nothing to
   * discard here: entering preview never pushes its own route (Piece 4's
   * own design - "no new route, no navigation-stack push"), so without
   * this, the physical back button pops `ArticleCompose`'s own single
   * history entry straight past the editor and back to whatever screen
   * opened it (confirmed live: landed on Profile, with the draft state
   * genuinely intact underneath but no way back to it via the button that
   * had just been pressed). The `beforeunload` effect above cannot cover
   * this case at all, and it is not a bug in it - `beforeunload` only ever
   * fires on a genuine document unload (tab close, refresh, a new
   * address), never on this app's own client-side route transitions,
   * since the document itself is never unloaded for those.
   *
   * Plain `window.history`, not React Navigation's own removal machinery
   * (`usePreventRemove`, tried and reverted earlier in this file's own
   * history for being needless complexity) - one extra history entry is
   * pushed the moment preview opens (`doPreview`, below), giving the
   * physical back button something of this screen's own to pop first
   * instead of leaving `ArticleCompose` in a single step.
   * `previewHistoryPushedRef` is the single source of truth for whether
   * that entry is currently sitting on top: set on entry, read and
   * cleared by whichever exit path fires first - this `popstate` listener
   * for the physical back button, or the "Back to edit" link's own
   * handler for a manual click, which pops it itself first so the stack
   * doesn't grow by one every time preview is opened and closed by hand.
   *
   * Deliberately not extended to the publish-success path: at that point
   * the user is leaving the screen for good via `onClose`, and a single
   * stray history entry left behind there is a minor, rare inconvenience
   * (one extra back-press after already leaving the composer) rather than
   * the data-loss risk this fix exists for - not worth racing against
   * whatever `onClose`'s own navigation does to the same history stack.
   */
  useEffect(() => {
    if (!IS_WEB) return
    const onPopState = () => {
      if (!previewHistoryPushedRef.current) return
      previewHistoryPushedRef.current = false
      setPreviewSnapshot(undefined)
    }
    window.addEventListener('popstate', onPopState)
    return () => window.removeEventListener('popstate', onPopState)
  }, [])

  const onToggleMark = (mark: 'bold' | 'italic' | 'strikethrough') => {
    if (mark === 'bold') editorBridge.toggleBold()
    else if (mark === 'italic') editorBridge.toggleItalic()
    else editorBridge.toggleStrike()
  }

  const onToggleUnderline = () => editorBridge.toggleUnderline()

  const onSetColor = (hex: string) => {
    // Finding 20 enforcement point: the toolbar's own menu only ever offers
    // PRESET_COLORS, which already passes this trivially, but that's the
    // caller being well-behaved, not a structural guarantee. This is the
    // one place a #color.value actually gets constructed, so the allowlist
    // has to live here, not upstream - any future caller (a free-text color
    // input, say) is protected for free instead of having to remember to
    // re-check.
    if (!isAllowedColorValue(hex)) return
    editorBridge.setColor(hex)
  }

  const onSetAlign = (align: 'left' | 'center' | 'right' | 'justify') =>
    editorBridge.setTextAlign(align)

  const onInsertList = () => editorBridge.toggleBulletList()

  const onSelectParagraphStyle = (style: ParagraphStyleId) =>
    editorBridge.setParagraphStyle(style)

  const onInsertHonorific = (codepoint: number) =>
    editorBridge.insertHonorific(codepoint)

  // TipTap's `Link` mark applies over a selection, not at a bare cursor
  // position - matches TenTap's own `LinkBridge` state model exactly
  // (`canSetLink: !editor.state.selection.empty`), not a new restriction
  // this screen invents. The old placeholder-text-with-no-selection path
  // (`[link text](https://)`) doesn't have a clean equivalent under a real
  // mark-based schema, so this asks for a selection first instead - a
  // real, if minor, product behavior change worth calling out, not a
  // silent regression.
  // The popover owns the URL now (see Toolbar's `InsertLinkPopover`); this
  // just forwards. It used to apply a hardcoded `'https://'` immediately,
  // which is what produced link-styled text pointing nowhere with no way to
  // set a destination. `setLink(null)` is TenTap's own remove.
  const onSetLink = (url: string) => editorBridge.setLink(url)
  // `''`, not `null` - upstream's own handler treats `null` as "cancelled"
  // and does nothing at all. `bridges/link.ts` accepts either, but `''`
  // also works against the stock bridge, so it can't silently regress if
  // that override is ever removed.
  const onRemoveLink = () => editorBridge.setLink('')
  const onLinkUnavailable = () =>
    Toast.show(_(msg`Select some text first, then apply the link.`), {
      type: 'info',
    })

  /**
   * Public URL for a blob on the PDS that actually holds it.
   *
   * `com.atproto.sync.getBlob` is a standard, unauthenticated atproto
   * endpoint (did+cid, both required) - a real, spec-compliant blob URL,
   * not a workaround.
   *
   * Built from `currentAccount.service` - the PDS this session is actually
   * signed in to - NOT the hardcoded `SUNNAHSKY_PDS_URL` both call sites
   * used to use. That hardcoding meant any non-production environment
   * produced URLs for blobs that only existed locally: they 404'd, rendered
   * as nothing, and looked exactly like "image insert is broken" / "the
   * cover image vanished".
   *
   * Note `pdsClient.service` is NOT the source: it exists on the type but
   * reads back `null` at runtime here, which produced a literal
   * `null/xrpc/...` URL - caught by tracing the real value rather than
   * trusting the property name.
   */
  const blobUrl = (blob: {ref?: {toString: () => string}; cid?: string}) => {
    // BlobRef is a union (current TypedBlobRef vs. the legacy string-cid
    // shape) - a freshly uploaded blob is always the former, but narrow
    // properly rather than assuming.
    const cid = 'ref' in blob && blob.ref ? blob.ref.toString() : blob.cid
    const base = (currentAccount?.service ?? '').replace(/\/$/, '')
    return `${base}/xrpc/com.atproto.sync.getBlob?did=${encodeURIComponent(pdsClient.assertDid)}&cid=${encodeURIComponent(cid ?? '')}`
  }

  /**
   * The toolbar button inserts the block *and* opens the picker, in one
   * action.
   *
   * This used to be two steps - insert an idle block, then open the picker
   * when the author clicked it - specifically to avoid a paragraph-split/
   * double-undo defect that opening on insert has (see `onDeleteImageBlock`).
   * That defect is real, but a separate, never fully root-caused regression
   * later made the click itself stop reliably opening the picker in some
   * sessions. Rather than keep chasing an unreproducible browser-activation
   * issue, the insert path was changed to never depend on a WebView click at
   * all: the toolbar is native RN, so it can call the picker directly, with
   * no round trip through the WebView needed to *start* it. Every other
   * interaction with a block - retrying an error, deleting - still goes
   * through the menu, which has a proven, repeatable track record.
   */
  const onInsertImage = () => {
    /*
     * Checked here rather than only inside the picker flow. Inserting a block
     * that can only ever fail is a worse experience than refusing up front:
     * the author would get an error on a block that should never have existed,
     * and would then have to delete it themselves.
     */
    if (bodyImages.length >= MAX_BODY_IMAGES) {
      Toast.show(
        _(
          msg`You can add up to 30 images to an article. Remove one before adding another.`,
        ),
        {type: 'error'},
      )
      return
    }
    const uploadId = `${Date.now()}-${Math.random().toString(36).slice(2)}`
    editorBridge.insertImageUpload(uploadId)
    void onImageBlockPicker(uploadId)
  }

  /**
   * Picks an image and uploads it into the placeholder `uploadId` names.
   *
   * Two callers: `onInsertImage` runs this immediately after inserting a
   * fresh block, and `onSelectImageForPlaceholder` reruns it from the menu's
   * "Select image" action - either a retry after an error, or picking for a
   * block that, for whatever reason, is still idle. Both end up here because
   * the flow itself doesn't care how the block got selected, only that it
   * has an `uploadId` to report progress against.
   *
   * The file never crosses the bridge: the picker and the authenticated upload
   * both stay here, and only the resulting URL goes back. See
   * `bridges/imageUpload.ts` for why that boundary is where it is.
   */
  const onImageBlockPicker = async (uploadId: string | null) => {
    if (!uploadId) return
    try {
      const images = await openPicker({selectionLimit: 1})
      const image = images[0]
      // Dismissing the picker leaves the block exactly as it was, idle and
      // clickable. Nothing to undo.
      if (!image) return

      /*
       * Measured, not taken from `image.size`. The picker fills that in with
       * `getDataUriSize(uri)`, which estimates a **data:** URI's length -
       * meaningful on native, meaningless on web, where the picker hands back
       * a `blob:` URL about fifty characters long. Every web image therefore
       * reported roughly 37 bytes and sailed past this check, only to be
       * rejected later by the PDS's own 5 MB limit with an error the author
       * could not act on.
       */
      const bytes = await measureImageBytes(image)
      if (bytes !== undefined && bytes > MAX_BODY_IMAGE_BYTES) {
        /*
         * Shown on the block, not as a toast (Figma 263:4172). With several
         * blocks in a document a toast cannot say *which* one failed, and it
         * disappears after three seconds - whereas the block is the thing that
         * failed and is already the retry target.
         */
        editorBridge.setImageError(
          uploadId,
          'too-large',
          `${(bytes / 1_000_000).toFixed(1)} MB`,
        )
        return
      }

      // Clears any error left from a previous attempt on this block.
      editorBridge.setImageError(uploadId, null)
      editorBridge.setImageUploading(uploadId, true)
      const {blob} = await uploadBlob(pdsClient, image.path, image.mime)
      const cid = blobCid(blob)

      /*
       * Retained before the editor is told about the image. This ref is the
       * only copy of the blob's mimeType and size until publish - the markdown
       * carries just a URL - so losing it means the image cannot be tethered
       * and silently vanishes from the published article.
       */
      if (cid) {
        setBodyImages(prev => [
          ...prev.filter(entry => blobCid(entry.image) !== cid),
          {image: blob, ...(image.fileName ? {name: image.fileName} : {})},
        ])
        setLocalImageUris(prev => ({...prev, [cid]: image.path}))
      }
      editorBridge.resolveImageUpload(uploadId, blobUrl(blob))
    } catch (err) {
      /*
       * The block carries the failure (Figma 263:4205) rather than a toast, for
       * the same reason as the size error above. It stays in place and stays
       * clickable, so retrying is one click on the thing that failed.
       */
      editorBridge.setImageError(uploadId, 'upload-failed')
      logger.error('ArticleCompose: image upload failed', {safeMessage: err})
    }
  }

  /**
   * Removes the image the menu was opened on.
   *
   * The blob ref is dropped from `bodyImages` at the same time, so the next
   * publish simply will not include it. Nothing is dereferenced on the PDS
   * here, because for an unpublished article nothing was ever tethered.
   */
  const onRemoveBodyImage = () => {
    const target = imageMenuTarget
    if (!target || target.kind !== 'image') return
    editorBridge.removeImageBySrc(target.src)
    if (target.cid) {
      setBodyImages(prev =>
        prev.filter(entry => blobCid(entry.image) !== target.cid),
      )
    }
    setImageMenuTarget(undefined)
  }

  /** Swaps the image in place, keeping the block and its position. */
  const onReplaceBodyImage = async () => {
    const target = imageMenuTarget
    setImageMenuTarget(undefined)
    if (!target || target.kind !== 'image') return
    try {
      const images = await openPicker({selectionLimit: 1})
      const image = images[0]
      if (!image) return
      // Same measured check as the insert path - `image.size` is not usable
      // on web, see `measureImageBytes`.
      const bytes = await measureImageBytes(image)
      if (bytes !== undefined && bytes > MAX_BODY_IMAGE_BYTES) {
        /*
         * Shown on the block (Figma 263:4172), same reasoning as the insert
         * path's own too-large check - and the same fix this one used to be
         * missing: a toast here quietly contradicted that reasoning, since
         * this block is exactly as much "the thing that failed and is
         * already the retry target" as an idle placeholder is.
         */
        editorBridge.setImageNodeError(
          target.src,
          'too-large',
          `${(bytes / 1_000_000).toFixed(1)} MB`,
        )
        return
      }
      const {blob} = await uploadBlob(pdsClient, image.path, image.mime)
      const cid = blobCid(blob)
      editorBridge.replaceImageSrc(target.src, blobUrl(blob))
      setBodyImages(prev => [
        // The outgoing ref goes now rather than at publish: leaving it would
        // keep tethering a blob the article no longer shows.
        ...prev.filter(
          entry =>
            blobCid(entry.image) !== target.cid && blobCid(entry.image) !== cid,
        ),
        {image: blob, ...(image.fileName ? {name: image.fileName} : {})},
      ])
      if (cid) {
        setLocalImageUris(prev => ({...prev, [cid]: image.path}))
      }
    } catch (err) {
      // Shown on the block (Figma 263:4205), not a toast - see the too-large
      // check above for why.
      editorBridge.setImageNodeError(target.src, 'upload-failed')
      logger.error('ArticleCompose: image replace failed', {safeMessage: err})
    }
  }

  /**
   * The placeholder menu's "Select image" action - retrying after an error,
   * or picking for a block that is otherwise still idle.
   *
   * Just reruns `onImageBlockPicker` against the same `uploadId` the menu was
   * opened on. The block itself doesn't need to know *why* it's being picked
   * for again; `onImageBlockPicker` already handles clearing a previous error
   * and re-entering the uploading state.
   */
  const onSelectImageForPlaceholder = () => {
    const target = imageMenuTarget
    setImageMenuTarget(undefined)
    if (!target || target.kind !== 'placeholder') return
    void onImageBlockPicker(target.uploadId)
  }

  /**
   * The placeholder menu's "Delete block" action.
   *
   * Deliberately does not attempt to rejoin a paragraph the block's insert
   * may have split - the project owner's own call, made explicitly to keep
   * this simple: repairing a split paragraph is something the author can do
   * themselves with an ordinary Backspace, the same as any other unwanted
   * paragraph break. See `bridges/imageUpload.ts`'s `deleteImageUpload`.
   */
  const onDeleteImageBlock = () => {
    const target = imageMenuTarget
    setImageMenuTarget(undefined)
    if (!target || target.kind !== 'placeholder' || !target.uploadId) return
    editorBridge.deleteImageUpload(target.uploadId)
  }

  /*
   * Latest handler for the click that happens inside the WebView, held in a
   * ref so the subscription effect below can mount exactly once. It used to
   * re-run on every render instead - meaning every render tore the
   * subscription down and rebuilt it - and this component re-renders on
   * essentially every keystroke (`onChange` below, and `bridgeState`). That
   * opened a real window, however brief, between teardown and rebuild where a
   * click arriving as an async message could land while nothing was
   * subscribed and do nothing at all - silently, with no error. This is the
   * same shape of bug as the filename race fixed earlier in this file: a
   * value read once instead of kept live. (There used to be a second ref
   * here, for a `picker` event - removed along with the event itself once
   * every WebView click started asking for the menu instead; see
   * `bridges/imageUpload.ts`'s doc comment for why.)
   */
  const onImageBlockMenuRef = useRef((_request: MenuRequest) => {})
  onImageBlockMenuRef.current = request => {
    /*
     * Translate the WebView-relative rect into page coordinates before
     * anchoring. `measureInWindow` is async, so the menu opens in its
     * callback rather than on a timer - the anchor must be mounted at its
     * final position before the dropdown measures against it, or web
     * positions it at the origin.
     */
    const surface = editorSurfaceRef.current
    if (!surface) {
      setImageMenuTarget(request)
      requestAnimationFrame(() => imageMenuControl.open())
      return
    }
    surface.measureInWindow((surfaceX, surfaceY) => {
      setImageMenuTarget({
        ...request,
        rect: {
          ...request.rect,
          x: request.rect.x + surfaceX,
          y: request.rect.y + surfaceY,
        },
      })
      requestAnimationFrame(() => imageMenuControl.open())
    })
  }

  /**
   * Clears `imageMenuTarget` whenever the menu closes for *any* reason.
   *
   * The four menu actions above already clear it themselves on success, but
   * that only covers choosing an item. Dismissing the menu without choosing
   * one - clicking the backdrop, pressing Escape - closes `imageMenuControl`
   * (Radix's own mechanism) without ever running any of those handlers, and
   * nothing else was watching for that. Left alone, `imageMenuTarget` stayed
   * set indefinitely: the absolutely-positioned trigger `View` it renders
   * covers the clicked block's last-known screen rect and keeps intercepting
   * pointer events there - through the surrounding text, since it has no idea
   * the document has moved on - until some other block's click happened to
   * overwrite it. That is the real cause of clicks silently failing to reach
   * the editor after opening a menu and clicking away from it, which is far
   * more likely to happen now that idle and error blocks open this same menu
   * too, not just filled images.
   *
   * Watches the closing edge specifically (`true` -> `false`), not just
   * "closed": `imageMenuTarget` is set one render before `imageMenuControl`
   * actually opens (see above - the rAF gap for `measureInWindow`), and a
   * naive `if (!isOpen)` would wipe it out again during that gap, before the
   * menu ever had a chance to open.
   */
  const wasImageMenuOpenRef = useRef(false)
  useEffect(() => {
    if (wasImageMenuOpenRef.current && !imageMenuControl.isOpen) {
      setImageMenuTarget(undefined)
    }
    // `isOpen` is declared optional on `DialogControlProps` (some other
    // implementation can leave it unset); this control always provides a
    // real boolean, but coerce anyway so the ref's own type stays boolean
    // rather than accepting a value this file never actually produces.
    wasImageMenuOpenRef.current = !!imageMenuControl.isOpen
  }, [imageMenuControl.isOpen])

  /**
   * Wires the click that happens inside the WebView back to this screen.
   *
   * The block and the image live in the editor document, but the menu is
   * React Native, so the click has to cross the bridge to reach anything that
   * can act on it. Subscribes once, for the lifetime of the screen - never
   * re-subscribed - and dispatches through the ref above so it is always
   * acting on current state without needing to churn.
   */
  useEffect(() => {
    return subscribeToImageBlockEvents({
      menu: request => onImageBlockMenuRef.current(request),
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /**
   * Publishes filenames into the editor whenever the set changes.
   *
   * Sanitised and truncated here rather than in the WebView so there is one
   * implementation of those rules for every surface - see
   * `#/lib/strings/filename`. Entries with no usable name are simply omitted,
   * and the block falls back to its format hint.
   */
  useEffect(() => {
    const names: Record<string, string> = {}
    for (const entry of bodyImages) {
      const cid = blobCid(entry.image)
      const name = displayFileName(entry.name)
      if (cid && name) names[cid] = name
    }
    editorBridge.setImageFileNames(names)
  }, [bodyImages, editorBridge])

  const onPressCoverImage = async () => {
    const images = await openPicker({selectionLimit: 1})
    const picked = images[0]
    if (!picked) return
    /*
     * The article and draft records cap a cover at 1 MB, well under what an
     * upload may be, so an oversized cover used to upload fine and then be
     * refused, with the whole article, at publish. It is shrunk to fit here
     * instead (see `prepareCoverImage`). Failures are reported with a Toast,
     * the same mechanism `doPreview`/`doConfirmPublish` use: a cover has no
     * block to carry an inline error the way a body image does.
     */
    let image: PickerImage
    try {
      image = await prepareCoverImage(picked)
    } catch (err) {
      logger.error('ArticleCompose: cover image resize failed', {
        safeMessage: err,
      })
      Toast.show(
        _(
          msg`This image couldn't be made small enough to use as a cover. Please choose a different one.`,
        ),
        {type: 'error'},
      )
      return
    }
    try {
      const {blob} = await uploadBlob(pdsClient, image.path, image.mime)
      /*
       * `blobUrl(blob)`, not `image.path` - `coverImagePreviewUri` feeds
       * `ArticleView`'s `coverImageSrc` prop, which goes through the shared
       * `ArticleImage` component's `cidFromSrc(src)` trust check (see its
       * own doc comment). `cidFromSrc` only ever recognizes a proper
       * Sunnahsky `getBlob` URL - a raw local picker path (a bare `blob:`
       * URI on web) matches neither of `cidFromSrc`'s patterns, so it
       * always returned no CID, and `ArticleImage` always rendered "Image
       * unavailable" for a freshly-uploaded cover image in preview, even
       * though the exact same blob published and displayed just fine once
       * tethered - a real bug, not a race or a lag. Body images never hit
       * this because their markdown `src` was always `blobUrl(blob)`
       * already, with the local file path tracked separately in
       * `localImageUris`, keyed by CID - the same mechanism reused here
       * directly rather than solved a second way, and the one
       * `setCoverImagePreviewUri` call site already doing this correctly
       * (`onSelectDraft`, for a reopened draft) confirms `blobUrl()` is the
       * right value, not `image.path`.
       */
      const cid = blobCid(blob)
      setCoverImage(blob)
      setCoverImagePreviewUri(blobUrl(blob))
      if (cid) {
        setLocalImageUris(prev => ({...prev, [cid]: image.path}))
      }
    } catch (err) {
      logger.error('ArticleCompose: cover image upload failed', {
        safeMessage: err,
      })
      Toast.show(cleanError(err), {type: 'error'})
    }
  }

  // Clears both halves together. `coverImage` is what gets published and
  // `coverImagePreviewUri` is what the button renders - leaving either
  // behind would either keep publishing a removed image or keep showing a
  // thumbnail for one that's gone. The uploaded blob itself isn't deleted:
  // nothing references it once it's off the record, and the PDS has no
  // caller-facing blob-delete anyway (unreferenced blobs are collected
  // server-side).
  const onRemoveCoverImage = () => {
    setCoverImage(undefined)
    setCoverImagePreviewUri(undefined)
  }

  const doSaveDraft = async (): Promise<{success: boolean}> => {
    try {
      const [{markdown, facets}, fresh] = await Promise.all([
        editorBridge.getMarkdownAndFacets(),
        editorBridge.getTitleAndSubtitle(),
      ])
      const {draftId: savedId} = await saveArticleDraft({
        state: {
          title: fresh.title,
          subtitle: fresh.subtitle,
          markdown,
          facets,
          metadata,
          coverImage,
          bodyImages,
        },
        existingDraftId: draftId,
      })
      setDraftId(savedId)
      setDraftUpdatedAt(new Date().toISOString())
      baselineRef.current = snapshot()
      setBodyTouched(false)
      return {success: true}
    } catch (err) {
      /*
       * Logged, not swallowed. This used to be a bare `catch {}`, which made a
       * failing draft save indistinguishable from any other - the author saw
       * "couldn't save" and the actual cause (a 501 from an endpoint the dev
       * PDS does not implement, a validation rejection, an expired session)
       * was gone. Anything that can fail for several unrelated reasons has to
       * say which one.
       */
      logger.error('ArticleCompose: draft save failed', {safeMessage: err})
      Toast.show(_(msg`Couldn't save draft. Please try again.`), {
        type: 'error',
      })
      return {success: false}
    }
  }

  // Clears in-progress content back to a fresh empty draft, without closing
  // the screen - used by the header Drafts button's "Discard" path (Cancel's
  // own "Discard" instead closes the screen entirely, via onClose).
  const onClearComposer = () => {
    const empty = {
      title: '',
      subtitle: '',
      metadata: {authors: [], translators: [], categories: [], tags: []},
      coverImage: undefined,
    }
    setTitle(empty.title)
    setSubtitle(empty.subtitle)
    editorBridge.setTitleAndSubtitle(empty.title, empty.subtitle)
    loadBodyContentSilently('', [])
    setMetadata(empty.metadata)
    setCoverImage(empty.coverImage)
    setCoverImagePreviewUri(undefined)
    setDraftId(undefined)
    setDraftUpdatedAt(undefined)
    // Defense-in-depth, not reachable in practice today - the Drafts UI
    // this is called from only renders when `!hasContent`, which is never
    // true while editing a published article.
    setEditingArticle(undefined)
    // Stale local file paths from whatever draft was just cleared have no
    // meaning for a fresh, empty one.
    setLocalImageUris({})
    setPreviewSnapshot(undefined)
    baselineRef.current = snapshotOf(empty)
    setBodyTouched(false)
  }

  const onSelectDraft = (summary: ArticleDraftSummary) => {
    const {draft} = summary
    const next = {
      title: draft.title ?? '',
      subtitle: draft.description ?? '',
      markdown: draft.markdown ?? '',
      metadata: {
        authors: draft.authors ?? [],
        translators: draft.translators ?? [],
        categories: draft.categories ?? [],
        tags: draft.tags ?? [],
      },
      coverImage: draft.coverImage,
      /*
       * The half of this screen's state that cannot be recovered from anything
       * else. Without it, reopening a draft and publishing writes an assets
       * record missing every image, and those blobs are then deleted.
       */
      bodyImages: draftBodyImages(draft.bodyImages),
    }
    setTitle(next.title)
    setSubtitle(next.subtitle)
    editorBridge.setTitleAndSubtitle(next.title, next.subtitle)
    loadBodyContentSilently(
      next.markdown,
      draftFacetsToEditorFacets(draft.facets),
    )
    setMetadata(next.metadata)
    setCoverImage(next.coverImage)
    setBodyImages(next.bodyImages)
    if (next.coverImage) {
      setCoverImagePreviewUri(blobUrl(next.coverImage))
    } else {
      setCoverImagePreviewUri(undefined)
    }
    setDraftId(summary.id)
    setDraftUpdatedAt(summary.updatedAt)
    // Defense-in-depth, not reachable in practice today - see the matching
    // note in `onClearComposer`.
    setEditingArticle(undefined)
    // A reopened draft's local file paths from whatever earlier session
    // uploaded them are long gone - `bodyImages` (the persisted blob refs)
    // survives the reopen fine, only these local paths don't. The preview's
    // "unavailable" fallback covers exactly this case.
    setLocalImageUris({})
    setPreviewSnapshot(undefined)
    baselineRef.current = snapshotOf({
      title: next.title,
      subtitle: next.subtitle,
      metadata: next.metadata,
      coverImage: next.coverImage,
    })
    setBodyTouched(false)
  }

  /*
   * Async, and pulls fresh title/subtitle before deciding - not the
   * mirrored `hasContent`/`isDirty` above. Cancel is an RN control outside
   * the WebView, so tapping it doesn't reliably blur the textarea inside the
   * iframe at all; without the pull, typing or pasting a title and hitting
   * Cancel inside the ~100ms debounce window would read the still-stale
   * (empty) mirror and close with no discard prompt, silently losing the
   * text. See `bridges/titleSubtitle.ts`'s own doc comment.
   */
  const onPressCancel = async () => {
    const fresh = await editorBridge.getTitleAndSubtitle()
    const {hasContent: freshHasContent, isDirty: freshIsDirty} =
      freshHasContentAndIsDirty(fresh)
    if (editingArticle) {
      if (freshIsDirty) {
        discardEditPromptControl.open()
      } else {
        onClose()
      }
      return
    }
    if (freshHasContent && (!draftId || freshIsDirty)) {
      discardPromptControl.open()
    } else {
      onClose()
    }
  }

  /**
   * Validates and freezes a snapshot, then enters the mandatory preview -
   * the actual write moved to `doConfirmPublish`, fired from the preview's
   * own confirm button. This is `doPublish`'s original first half, unchanged
   * up through the fresh-pull title check; nothing about the validation
   * itself changed, only what happens once it passes.
   */
  const doPreview = async () => {
    // The mirrored `title` here is a hint, not authoritative - it gates the
    // button's own visual enabled state, cheaply, without a round trip on
    // every keystroke. The real check, against a fresh pull, happens below
    // before anything actually gets written.
    if (isPublishing || !title.trim() || bridgeState.empty) return
    if (editingArticle && !isDirty) return
    setIsPublishing(true)
    try {
      const [{markdown, facets}, fresh] = await Promise.all([
        editorBridge.getMarkdownAndFacets(),
        editorBridge.getTitleAndSubtitle(),
      ])
      /*
       * Re-checked against the fresh pull specifically, not the mirrored
       * `title` already checked above - that mirror is debounced 100ms, so
       * typing a title, deleting it, and tapping Publish inside that window
       * would otherwise pass the earlier check on a stale non-empty value
       * and only discover the real (empty) title here, after the button
       * press already went through. `title` is `required` in
       * `site.standard.document` - publishing with it silently empty is a
       * PDS rejection at best, not something to let through and hope.
       */
      if (!fresh.title.trim()) {
        Toast.show(_(msg`Please add a title before publishing.`), {
          type: 'error',
        })
        return
      }
      if (IS_WEB) {
        window.history.pushState(null, '')
        previewHistoryPushedRef.current = true
      }
      setPreviewSnapshot({
        title: fresh.title.trim(),
        subtitle: fresh.subtitle.trim(),
        markdown,
        facets: facets,
      })
    } catch (err) {
      // Same reasoning as `doConfirmPublish`'s own catch: the title-empty
      // case above is an expected, already-messaged outcome, but a failure
      // reaching here means the editor bridge round-trip itself broke
      // (e.g. the WebView failed to respond) - that must not fail silently
      // just because entering preview happens to touch no network.
      logger.error('ArticleCompose: entering preview failed', {
        safeMessage: err,
      })
      Toast.show(cleanError(err), {type: 'error'})
    } finally {
      setIsPublishing(false)
    }
  }
  const onPressPreview = requireStriker(() => void doPreview())

  /**
   * The actual write, fired from the preview screen's own "Publish"/"Update"
   * button - `doPublish`'s original second half, reading from the frozen
   * snapshot instead of pulling fresh from the editor bridge (the editor is
   * not even mounted while the preview is showing).
   */
  const doConfirmPublish = async () => {
    if (!previewSnapshot || isPublishing) return
    setIsPublishing(true)
    try {
      const editing: ArticleEditRef | undefined = editingArticle
        ? {
            documentUri: editingArticle.uri,
            documentRkey: editingArticle.rkey,
            documentCid: editingArticle.cid,
            publishedAt: editingArticle.publishedAt,
          }
        : undefined
      const {documentUri} = await publishArticle({
        pdsClient,
        draft: {
          title: previewSnapshot.title,
          description: previewSnapshot.subtitle || previewSnapshot.title,
          markdown: previewSnapshot.markdown,
          flavor,
          facets: previewSnapshot.facets,
          tags: metadata.tags,
          categories: metadata.categories.length
            ? metadata.categories
            : undefined,
          authors: metadata.authors.length ? metadata.authors : undefined,
          translators: metadata.translators.length
            ? metadata.translators
            : undefined,
          contributors: [],
          coverImage,
          bodyImages,
        },
        editing,
      })
      Toast.show(
        editingArticle
          ? _(msg`Article updated!`)
          : _(
              msg`Article published! It may take a few seconds to appear on your profile.`,
            ),
        {type: 'success'},
      )
      const did = currentAccount?.did
      if (did) {
        /*
         * `PUBLIC_ARTICLE_RQKEY` is the reader's own query (`Article/
         * index.tsx`, unauthenticated, `STALE.MINUTES.FIVE`) - a completely
         * separate cache entry from `RQKEY`/`DOCUMENT_RQKEY` above, keyed
         * by `did`+`rkey` rather than by document URI. Missing here meant
         * an edit that added a body image (or changed anything else) could
         * silently keep serving the pre-edit reader page for up to five
         * minutes to anyone who had already opened it - including the
         * author, testing their own just-published change. Invalidated
         * unconditionally on both branches: a first-time publish can't
         * have a stale entry yet (nobody could have viewed it before it
         * existed), but there's no reason to special-case that away.
         */
        const rkey = editingArticle
          ? editingArticle.rkey
          : documentUri.split('/').pop()
        if (rkey) {
          void queryClient.invalidateQueries({
            queryKey: PUBLIC_ARTICLE_RQKEY(did, rkey),
          })
        }
        // `useAuthorArticlesQuery` now reads documents straight off the PDS
        // (see its own doc comment) - no AppView indexing to wait out, so
        // both a fresh publish and an edit can invalidate immediately.
        void queryClient.invalidateQueries({queryKey: RQKEY(did)})
        if (editingArticle) {
          void queryClient.invalidateQueries({
            queryKey: DOCUMENT_RQKEY(documentUri),
          })
        }
      }
      // Best-effort cleanup, matching the post Composer's own post-publish
      // draft cleanup - the article already published successfully, so a
      // failure here is logged, not surfaced (see the mutation's onError).
      // Unchanged by the edit-published-article mode: `draftId` and
      // `editingArticle` are mutually exclusive by construction, since the
      // Drafts UI is only reachable when `!hasContent`, never true here.
      if (draftId) {
        cleanupPublishedDraft({draftId})
      }
      // Only a fresh publish gets the share prompt - editing never does
      // (and never will): most edits are typo fixes, and prompting on each
      // one either trains people to dismiss it or produces feed spam.
      // Reshare stays available, author-initiated, from the Articles tab.
      if (editingArticle) {
        setPreviewSnapshot(undefined)
        onClose()
      } else {
        setJustPublished({documentUri, title: previewSnapshot.title})
        setPreviewSnapshot(undefined)
        shareArticlePromptControl.open()
      }
    } catch (err) {
      // Unlike `doPreview`'s own validation checks (caught before any
      // network round-trip, so the cause is always known in advance), a
      // failure here comes from the actual write - the PDS rejecting the
      // batch, a dropped connection, a stale edit ref - so the message has
      // to come from whatever was actually thrown, rather than a single
      // generic string that would hide which of those it was.
      // `publishErrorMessage` puts the PDS's schema errors into plain
      // English and passes everything else to `cleanError`, as the main
      // post Composer's publish failure path does.
      logger.error('ArticleCompose: article publish failed', {
        safeMessage: err,
      })
      Toast.show(publishErrorMessage(err), {type: 'error'})
    } finally {
      setIsPublishing(false)
    }
  }
  const onPressConfirmPublish = requireStriker(() => void doConfirmPublish())

  // Both live-computed on the web side from `editor.getText()`/live editor
  // state and pushed here via the same debounced state-sync `useBridgeState`
  // already relies on for every other bridge - no separate polling, no
  // approximation the old ref-based cursor tracking needed.
  const wordCount = bridgeState.wordCount
  const charCount = bridgeState.charCount
  const activeParagraphStyle = bridgeState.activeParagraphStyle

  // Live formatting state for the toolbar's own active indicators. Arrives on
  // the same debounced push as the counts above (including on plain cursor
  // moves, via `onSelectionUpdate`), so moving the caret into already-bold
  // text lights the Bold button without any extra plumbing. Bold/italic/
  // strike/underline and the list flag are TenTap's own bridge state -
  // underline included, since `bridges/underline.ts` is a `.clone()` of
  // TenTap's bridge and keeps its active-state tracking; `activeTextAlign`
  // comes from this app's own `textAlign` bridge.
  const activeMarks = {
    bold: bridgeState.isBoldActive,
    italic: bridgeState.isItalicActive,
    underline: bridgeState.isUnderlineActive,
    strikethrough: bridgeState.isStrikeActive,
  }
  const activeTextAlign = bridgeState.activeTextAlign
  const isBulletListActive = bridgeState.isBulletListActive

  // Allowlist-checked before it reaches the toolbar, not after: this value
  // originates in the document, and a document authored by a different
  // client can carry any color string at all. The toolbar renders it as a
  // real fill on the trigger swatch, so it's untrusted input reaching a
  // style - the same fail-closed treatment `onSetColor` already applies on
  // the way in (`colorAllowlist.ts`). Anything unrecognised reads as "no
  // color", which is exactly how the renderer treats it too.
  const rawActiveColor = bridgeState.activeColor
  const activeColor =
    typeof rawActiveColor === 'string' && isAllowedColorValue(rawActiveColor)
      ? rawActiveColor
      : undefined

  // Never "Save and close" while editing a published article - there's
  // deliberately no save-progress-without-publishing path for that mode
  // (see the plan's Context section), so the label would be misleading.
  const closeLabel =
    isDirty && !editingArticle ? _(msg`Save and close`) : _(msg`Close`)

  // Shared by both `<Toolbar>` instances below (desktop's single `part="all"`
  // one and, on mobile, the separate `part="top"`/`part="bottom"` pair) -
  // every prop the combined toolbar ever needed, since each instance now
  // just ignores whatever its own `part` doesn't render.
  const toolbarProps = {
    isDisabled: activeField !== 'body',
    onToggleMark,
    onToggleUnderline,
    activeParagraphStyle,
    activeMarks,
    activeTextAlign,
    activeColor,
    isBulletListActive,
    onSelectParagraphStyle,
    onInsertList,
    onSetAlign,
    onSetColor,
    onInsertHonorific,
    onRequestBodyFocus: () => {
      // Regains WebView focus without repositioning the cursor - see
      // `REFOCUS_WITHOUT_MOVING_CURSOR`'s own doc comment for why that's
      // `0`, not TipTap's more obvious-looking `null`. Unlike the old
      // plain-`TextInput` model, there's no caret position to manually
      // restore here: `HonorificBridge`'s own `onBridgeMessage` already
      // does `.chain().focus().insertContent(text).run()` on the web side,
      // in the same transaction as the insert, so the cursor is already
      // exactly where it should be by the time this runs.
      editorBridge.focus(REFOCUS_WITHOUT_MOVING_CURSOR)
    },
    activeLink: bridgeState.activeLink,
    canSetLink: bridgeState.canSetLink,
    isLinkActive: bridgeState.isLinkActive,
    onSetLink,
    onRemoveLink,
    onLinkUnavailable,
    onInsertImage: () => void onInsertImage(),
  }

  // Mobile's bottom toolbar sits right above the on-screen keyboard, but
  // Chrome's default viewport behavior (`interactive-widget=resizes-visual`,
  // since web/index.html's own meta tag doesn't opt into `resizes-content`)
  // never shrinks the layout viewport for the keyboard - only the *visual*
  // viewport does - so without this, the keyboard just covers the toolbar.
  // See `keyboardLayout.ts` for the full dependency model and why the
  // arithmetic itself lives there, unit-tested, rather than inline here.
  //
  // `GBOARD_STRIP_BUFFER` (26px) is a flat, empirically-tuned constant, not
  // a measurement - no viewport API reports Gboard's own persistent toolbar
  // strip (emoji/GIF/translate/mic, drawn above its key rows), confirmed
  // live: it left the bottom ~20-30% of this toolbar clipped under that
  // strip with no compensation. `navigator.virtualKeyboard` (the one real
  // API built for exactly this - `overlaysContent = true` unlocks
  // `boundingRect`/`geometrychange`, which would report the strip as part
  // of the same occluded region, no fitted constant needed) was tried and
  // properly re-checked with a live on-device diagnostic, not just inferred
  // from "no observed change" - `'virtualKeyboard' in navigator` is
  // genuinely `false` on this Chrome/Android build, confirmed directly.
  // This constant is Chromium/Android-specific in more ways than just that
  // absence, and every one of them is a real cost of keeping it rather than
  // a settled value: Gboard's strip can be turned off in the keyboard's own
  // settings (the buffer then becomes a 26px gap that shouldn't be there),
  // its actual height scales with display density and system font size,
  // other Android keyboards (Samsung Keyboard, SwiftKey) occlude
  // differently, and iOS has no equivalent strip *and* supports none of
  // `interactive-widget` - this whole model (this constant, and the
  // `offsetTop`-based pan tracking below and on the top toolbar) needs an
  // explicit iOS answer of its own before that platform is tested, not an
  // assumption that it carries over.
  const GBOARD_STRIP_BUFFER = 26
  const [toolbarLift, setToolbarLift] = useState(0)
  useEffect(() => {
    if (!IS_WEB || !window.visualViewport) return
    const viewport = window.visualViewport
    const onUpdate = () => {
      const lift = computeBottomToolbarLift({
        windowInnerHeight: window.innerHeight,
        panOffset: viewport.offsetTop,
        visualViewportHeight: viewport.height,
        gboardStripBuffer: GBOARD_STRIP_BUFFER,
      })
      setToolbarLift(lift)
    }
    onUpdate()
    viewport.addEventListener('resize', onUpdate)
    viewport.addEventListener('scroll', onUpdate)
    // `window.innerHeight` itself can shift independently of a
    // `visualViewport` event (Chrome's URL bar auto-hiding on scroll) -
    // without this, `onUpdate` could read a momentarily-stale
    // `innerHeight` against a fresh viewport reading and land a frame or
    // two off during the keyboard's own open/close animation.
    window.addEventListener('resize', onUpdate)
    return () => {
      viewport.removeEventListener('resize', onUpdate)
      viewport.removeEventListener('scroll', onUpdate)
      window.removeEventListener('resize', onUpdate)
    }
  }, [])

  // Guards against Android adding phantom scrollable space to `html`/`body`
  // while the keyboard's open, which can otherwise leave a few extra
  // pixels of scroll room that the toolbar math above has no way to see or
  // account for. `clip` (not `hidden`) specifically: it forbids scrolling
  // without creating its own scroll container, so this can't itself change
  // how the page's real layout behaves - it only closes off that escape
  // hatch. Scoped to this screen's own lifetime and restored on unmount,
  // since `html`/`body` are shared with every other screen in the app.
  useEffect(() => {
    if (!IS_WEB) return
    const html = document.documentElement
    const {body} = document
    const prevHtmlOverflow = html.style.overflow
    const prevBodyOverflow = body.style.overflow
    html.style.overflow = 'clip'
    body.style.overflow = 'clip'
    return () => {
      html.style.overflow = prevHtmlOverflow
      body.style.overflow = prevBodyOverflow
    }
  }, [])

  /*
   * Reported symptom: opening the editor while unfocused, the *first*
   * click on the body area visibly scrolled but never actually focused
   * the WebView (no caret appeared) - only a second click did.
   *
   * Five attempts, kept here as a pointer rather than the full history
   * (see git blame / `Sunnahsky_Engineering_Notes.md` for the
   * complete account, since it's long enough to be its own liability to
   * maintain inline): the first two tried firing `editorBridge.focus(...)`
   * from progressively-more-reliable parent-side triggers
   * (`onMouseDown`, then `window` `blur`); the third gated that call on
   * the library's own `EditorReady` signal instead of guessing when the
   * WebView was safe to message; the fourth found and fixed a real,
   * source-confirmed TipTap bug (`commands/focus.ts`'s
   * `view.hasFocus() && position === null` guard silently no-ops the
   * bare/`null` form of `.focus()`, a known community footgun -
   * `scrumpy/tiptap#389` - fixed here via `REFOCUS_WITHOUT_MOVING_CURSOR`,
   * see its own doc comment, still used below by the unrelated
   * honorific-focus-restore call).
   *
   * The fifth attempt is why none of that machinery survives here. Live
   * testing with a diagnostic `mousedown` listener (kept below, now
   * load-bearing rather than diagnostic - see its own comment) showed the
   * native click, on its own, already correctly focuses the WebView *and*
   * places the caret at the clicked position - confirmed directly
   * (`hasFocusBefore: false` immediately before, a correct click-accurate
   * caret immediately after). The parent-side `.focus(...)` mechanism
   * built across attempts 1-4 was not fixing a real gap in that behavior;
   * it was *racing* it. `.focus(...)`'s own `delayedFocus()` calls
   * `view.focus()` unconditionally with no `{preventScroll: true}` except
   * on Safari (TipTap's own source special-cases only that one browser,
   * for exactly this class of bug) - so calling it at all, even
   * perfectly-timed, forced a redundant native scroll-into-view
   * independent of where the selection actually was, and calling it
   * *before* ProseMirror's own click handler had finished (a real,
   * observed race, not a hypothetical one) intermittently overwrote a
   * correct, freshly-placed selection with whatever the selection was
   * *before* the click - the document start, for a fresh load. Both
   * symptoms disappeared once every parent-side `.focus(...)` call tied to
   * this flow was removed. Not fully explained: *why* the native click
   * needed the `mousedown` listener's mere presence in the first place -
   * plausibly the same "click on an unfocused iframe doesn't always
   * deliver as a real content event" quirk the whole investigation started
   * from, with something about a registered capturing-phase listener on
   * the document changing how the browser dispatches it. Flagged as an
   * open question rather than asserted, since it wasn't independently
   * confirmed the way the rest of this account was - removing the
   * `mousedown` listener entirely was not tried, specifically to avoid
   * re-opening a bug that took five rounds to close.
   */

  // Mobile: the top toolbar itself (not the header or Article details above
  // it) is pinned to the top of the screen - confirmed live that without
  // this, focusing the body scrolls it out of view along with Title.
  // Measures its own rendered height so the WebView below can be given
  // matching top space instead of rendering underneath it.
  const [topChromeHeight, setTopChromeHeight] = useState(0)

  // The toolbar's *resting* position - right below the header and Article
  // details, which stay in normal flow and are never pinned. Without this
  // baseline, the toolbar's `top` (below) had nothing to rest on when
  // there's no pan to track (`topChromeOffset` is `0` with nothing
  // focused), so the fixed toolbar snapped to the literal top of the
  // screen and painted over the still-visible header instead of sitting
  // beneath it - confirmed live on first page load, before anything is
  // focused.
  const [headerHeight, setHeaderHeight] = useState(0)

  // `position: fixed; top: 0` alone was *not* enough - confirmed live, no
  // change at all. The keyboard opening doesn't move `window.scrollY` (a
  // real document scroll, which `fixed` is already immune to - proven by
  // `ComposerOverlay`'s own `fixed` wrapper already protecting everything
  // inside it, this block included). It's a *visual viewport pan*: the
  // browser shifts which slice of the same, unchanged layout viewport is
  // currently on screen, to keep the focused caret visible, without
  // reflowing anything. `position: fixed` anchors to the *layout*
  // viewport, not the visual one, so a static `top: 0` renders at the
  // layout viewport's true top - which the pan has now moved *above* the
  // visible slice, off-screen. `visualViewport.offsetTop` is exactly that
  // pan distance; tracking it into `top` is the same fix the bottom
  // toolbar already needed for the identical reason, just never carried
  // over to this side.
  const [topChromeOffset, setTopChromeOffset] = useState(0)
  useEffect(() => {
    if (!IS_WEB || !window.visualViewport) return
    const viewport = window.visualViewport
    const onUpdate = () => setTopChromeOffset(viewport.offsetTop)
    onUpdate()
    viewport.addEventListener('resize', onUpdate)
    viewport.addEventListener('scroll', onUpdate)
    return () => {
      viewport.removeEventListener('resize', onUpdate)
      viewport.removeEventListener('scroll', onUpdate)
    }
  }, [])

  const content = (
    <View
      style={[
        a.flex_1,
        t.atoms.bg,
        gtMobile && [
          {
            width: 1000,
            maxWidth: '100%',
            height: '85%',
            minHeight: 480,
            maxHeight: 900,
          },
          a.rounded_md,
          a.overflow_hidden,
        ],
      ]}>
      <View
        onLayout={e => {
          if (!gtMobile) setHeaderHeight(e.nativeEvent.layout.height)
        }}>
        <View
          style={[
            a.flex_row,
            a.align_center,
            a.justify_between,
            {paddingHorizontal: 18, paddingVertical: 12},
            a.border_b,
            t.atoms.border_contrast_low,
          ]}>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={closeLabel}
            accessibilityHint=""
            onPress={() => void onPressCancel()}>
            <Text
              style={[
                a.text_md,
                a.font_medium,
                {color: t.palette.primary_600},
              ]}>
              {closeLabel}
            </Text>
          </Pressable>
          <View style={[a.flex_row, a.align_center, {gap: 18}]}>
            {!hasContent ? (
              <DraftsButton
                onSelectDraft={onSelectDraft}
                onSaveDraft={doSaveDraft}
                onDiscard={onClearComposer}
                getFreshHasContentAndIsDirty={async () =>
                  freshHasContentAndIsDirty(
                    await editorBridge.getTitleAndSubtitle(),
                  )
                }
                isEditingDraft={!!draftId}
              />
            ) : editingArticle ? (
              <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
                <Trans>
                  Published on{' '}
                  {niceDate(i18n, editingArticle.publishedAt, 'medium', 'none')}
                  .
                </Trans>
                {editingArticle.updatedAt && (
                  <Trans>
                    {' '}
                    Last Modified:{' '}
                    {niceDate(i18n, editingArticle.updatedAt, 'medium', 'none')}
                    .
                  </Trans>
                )}
                {isDirty && <Trans> • Unsaved edits.</Trans>}
              </Text>
            ) : draftId && !isDirty ? (
              <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
                <Trans>
                  Draft saved{' '}
                  {niceDate(
                    i18n,
                    draftUpdatedAt ?? new Date().toISOString(),
                    'medium',
                    'none',
                  )}
                  .
                </Trans>
              </Text>
            ) : (
              <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
                <Trans>Unsaved draft.</Trans>
              </Text>
            )}
            <Button
              // Unconditionally "Preview" now - this button only ever enters
              // the mandatory preview, it never publishes directly. The real
              // "Publish"/"Update" label (and its own loading state) moved to
              // the preview's own confirm button.
              label={_(msg`Preview`)}
              variant="solid"
              color="primary"
              size="small"
              disabled={
                isPublishing ||
                !title.trim() ||
                bridgeState.empty ||
                (!!editingArticle && !isDirty)
              }
              onPress={onPressPreview}>
              <ButtonText>
                <Trans>Preview</Trans>
              </ButtonText>
            </Button>
          </View>
        </View>

        <Prompt.Outer control={discardPromptControl}>
          <Prompt.Content>
            <Prompt.TitleText>
              {draftId ? (
                <Trans>Save changes?</Trans>
              ) : (
                <Trans>Save draft?</Trans>
              )}
            </Prompt.TitleText>
            <Prompt.DescriptionText>
              {draftId ? (
                <Trans>
                  You have unsaved changes to this draft, would you like to save
                  them?
                </Trans>
              ) : (
                <Trans>
                  Would you like to save this as a draft to edit later?
                </Trans>
              )}
            </Prompt.DescriptionText>
          </Prompt.Content>
          <Prompt.Actions>
            <Prompt.Action
              cta={draftId ? _(msg`Save changes`) : _(msg`Save draft`)}
              onPress={() =>
                void doSaveDraft().then(({success}) => {
                  if (success) onClose()
                })
              }
              color="primary"
            />
            <Prompt.Action
              cta={_(msg`Discard`)}
              onPress={onClose}
              color="negative_subtle"
            />
            <Prompt.Cancel cta={_(msg`Keep editing`)} />
          </Prompt.Actions>
        </Prompt.Outer>

        <Prompt.Basic
          control={discardEditPromptControl}
          title={_(msg`Discard changes?`)}
          description={_(
            msg`You have unsaved edits to this article. If you close now, they will be lost.`,
          )}
          onConfirm={onClose}
          confirmButtonCta={_(msg`Discard`)}
          confirmButtonColor="negative"
          cancelButtonCta={_(msg`Keep editing`)}
        />

        <Metadata
          value={metadata}
          onChange={setMetadata}
          coverImagePreviewUri={coverImagePreviewUri}
          onPressCoverImage={() => void onPressCoverImage()}
          onRemoveCoverImage={onRemoveCoverImage}
          wordCount={wordCount}
          charCount={charCount}
        />
      </View>

      {/*
       * Only the toolbar itself stays pinned on mobile - not the header or
       * Article details above it, which stay in normal flow. See
       * `keyboardLayout.ts` for the full dependency model behind `top`
       * below - written down there, and unit-tested, specifically because
       * this exact formula was the site of a real double-counting bug
       * during development.
       */}
      <View
        onLayout={e => {
          if (!gtMobile) setTopChromeHeight(e.nativeEvent.layout.height)
        }}
        style={[
          t.atoms.bg,
          !gtMobile && [
            a.fixed,
            {
              top: computeTopToolbarTop(headerHeight, topChromeOffset),
              left: 0,
              right: 0,
              zIndex: 1,
            },
          ],
        ]}>
        <View
          style={[
            gtMobile
              ? {paddingHorizontal: 18, paddingTop: 18}
              : // Figma 58:5162, "Text editor toolbar": px-18/py-12 plus its
                // own border-bottom - unlike desktop, which gets its bottom
                // spacing from the sibling below instead (see that View's
                // own `marginTop` comment) and has no border of its own
                // here at all. Putting the border on this fixed, always-
                // correctly-positioned block - rather than relying on the
                // WebView region's own `border_t` below to visually stand
                // in for it - is what keeps the line from disappearing
                // when the keyboard pans this toolbar while focused: it's
                // intrinsic to this box's own edge, not a second element
                // that has to stay pixel-aligned with it.
                [
                  {paddingHorizontal: 18, paddingVertical: 12},
                  a.border_b,
                  t.atoms.border_contrast_low,
                ],
          ]}>
          {/*
           * Desktop keeps the single combined toolbar (`part="all"`, the
           * default) exactly where it's always been. Mobile splits it
           * (Figma 58:5153): this instance becomes `part="top"`, and a
           * second `part="bottom"` instance renders further down, right
           * above the word count row - both share every prop below via
           * `toolbarProps` (see there for why), so the split changes only
           * which controls each instance shows, never their behavior.
           */}
          <Toolbar {...toolbarProps} part={gtMobile ? 'all' : 'top'} />
        </View>
      </View>

      <View
        style={[
          a.flex_1,
          // Desktop: reproduces the `gap: 12` this used to share with the
          // toolbar above, back when both were flex children of one
          // wrapper - now separate siblings (the toolbar moved into the
          // fixed group above), so this owns that spacing directly.
          gtMobile && {marginTop: 12},
          // Mobile: see `keyboardLayout.ts`'s `computeWebViewMarginTop`.
          // `topChromeHeight` alone was this session's earlier fix for a
          // double-counting bug - but it was incomplete: once focusing the
          // body pans the toolbar past its resting spot on the header
          // (`topChromeOffset > headerHeight`, the same condition its own
          // `top` above reacts to), the toolbar sits further down-screen
          // than a static margin can know about, and the WebView's first
          // line - the caret included - renders under it. Both
          // `headerHeight` and `topChromeOffset` belong in this formula
          // too now, just not as a flat re-addition of either.
          !gtMobile &&
            topChromeHeight > 0 && {
              marginTop: computeWebViewMarginTop({
                headerHeight,
                topToolbarHeight: topChromeHeight,
                panOffset: topChromeOffset,
              }),
            },
          // Mirrors the `marginTop` above, for the same reason on the other
          // edge: the bottom toolbar's own lift (`toolbarLift`, above) is a
          // `transform`, which repositions what's *painted* without ever
          // touching layout - so without this, the WebView's box stays the
          // same size it was unlifted, and the toolbar's lifted position
          // paints directly over its own bottom `toolbarLift` px. A real
          // `marginBottom` here actually shrinks the box to match, so the
          // WebView's own last visible line - and ProseMirror's own native
          // scroll-into-view, keyed off that same box - never sits behind
          // it. Confirmed live: a purely web-side fix (CSS `scroll-padding`,
          // a manual scroll correction, even reconfiguring ProseMirror's own
          // `scrollMargin`/`scrollThreshold`) never worked, because none of
          // those change what the WebView's box itself actually measures as
          // - this native-side margin is the one lever that does.
          !gtMobile && toolbarLift > 0 && {marginBottom: toolbarLift},
        ]}>
        {/*
         * Edge-to-edge, not boxed (Figma 261:3974) - a plain rule above and
         * below, spanning this region's full width with no horizontal
         * padding of its own. Title, Sub-title, and the body all live inside
         * the WebView now (`editor-web/index.html`'s `.composer-column`,
         * `#article-fields`) - this `View` just frames it; the 720px reading
         * column, its centering, and its own scroll all belong to that
         * document, not to anything in this file. Three rounds of an outer
         * `ScrollView` trying to stay frame-synced with a separately-
         * measured WebView (a height-reporting bridge, an `overflow: hidden`
         * lock, a scroll-position-lock listener) turned up enough native/web
         * bridge sharp edges to be worth removing entirely in favor of
         * this - the WebView is a plain `flex_1` box again, scrolling
         * itself, exactly like any ordinary web page.
         */}
        <View
          style={[
            a.flex_1,
            // Desktop only - its toolbar has no border-bottom of its own
            // (see above), so this rule stands in for it. Mobile's fixed
            // toolbar carries its own border-bottom instead; keeping this
            // one too would double the line directly beneath it.
            gtMobile && a.border_t,
            a.border_b,
            t.atoms.border_contrast_low,
          ]}>
          {/*
           * `editorSurfaceRef` is unrelated to this region's own layout -
           * kept for `onImageBlockMenuRef`'s `measureInWindow` call, which
           * positions the image edit menu against wherever this View
           * actually sits on screen. `collapsable={false}` keeps it a real
           * native view node on Android, which `measureInWindow` needs.
           */}
          <View ref={editorSurfaceRef} style={[a.flex_1]} collapsable={false}>
            <RichText editor={editorBridge} style={[a.flex_1]} />
          </View>
        </View>
      </View>
      {/* On mobile the word count lives inside the Article details
      accordion instead (Figma 58:5227's own hint text) - see `Metadata`'s
      `wordCount`/`charCount` props above. This wrapper's own transform
      exists purely to lift the second toolbar bar above the on-screen
      keyboard now; see `toolbarLift`'s own comment above `content`. */}
      <View
        style={[
          !gtMobile &&
            toolbarLift > 0 && {transform: [{translateY: -toolbarLift}]},
          !gtMobile &&
            toolbarLift > 0 &&
            // Without this, an inherited/default CSS transition eases the
            // transform instead of applying it instantly, so it visibly
            // lags a beat behind the keyboard's own open/close animation
            // instead of tracking it in lockstep.
            web({transitionProperty: 'none' as unknown as number}),
        ]}>
        {/* Desktop only - mobile's own copy of this text lives in the
        Article details accordion instead (see above). Not inside the
        padding:18 block above - its own px:18/py:12 matches the top bar's,
        but it never participates in the edge-to-edge strip's flex sizing. */}
        {gtMobile && (
          <View
            style={[
              a.flex_row,
              a.align_center,
              a.justify_center,
              {paddingHorizontal: 18, paddingVertical: 12},
            ]}>
            <Text style={[a.flex_1, a.text_sm, t.atoms.text_contrast_medium]}>
              <Trans>
                Word count: {wordCount} • Character count: {charCount}
              </Trans>
            </Text>
          </View>
        )}
        {/* Mobile-only second bar (Figma 58:5191) - the paragraph-style/marks/
        honorific controls the top toolbar gave up, placed last so they sit
        right above the keyboard rather than up under "Article details". */}
        {!gtMobile && (
          <View
            style={[
              a.border_t,
              t.atoms.border_contrast_low,
              {paddingHorizontal: 18, paddingVertical: 12},
            ]}>
            <Toolbar {...toolbarProps} part="bottom" />
          </View>
        )}
      </View>
    </View>
  )

  const previewDoc = useMemo(() => {
    if (!previewSnapshot) return undefined
    return applyFacetsToParsedDoc(
      manager,
      previewSnapshot.markdown,
      previewSnapshot.facets,
    ).doc
  }, [previewSnapshot])

  /*
   * The preview shows the author's own account identity in the header, not a
   * separate "publication" - this composer has no concept of one distinct
   * from the account itself. `useProfileQuery` is the same hook other
   * screens already use for "this account's own profile"; a short
   * `STALE.SECONDS.FIFTEEN` is fine here since this is a read-only preview
   * of the author's own already-loaded session.
   */
  const ownProfile = useProfileQuery({did: currentAccount?.did})
  const openLink = useOpenLink()

  const previewContent = previewSnapshot && previewDoc && (
    <View style={[a.flex_1, t.atoms.bg]}>
      <ScrollView style={[a.flex_1]} contentContainerStyle={[a.align_center]}>
        <ArticleView
          variant="preview"
          publicationName={
            ownProfile.data?.displayName ||
            ownProfile.data?.handle ||
            currentAccount?.handle ||
            ''
          }
          publicationAvatar={ownProfile.data?.avatar}
          handle={currentAccount?.handle ?? ''}
          title={previewSnapshot.title}
          subtitle={previewSnapshot.subtitle || undefined}
          authors={metadata.authors}
          translators={metadata.translators}
          date={new Date()}
          coverImageSrc={coverImagePreviewUri}
          doc={previewDoc}
          localImageUris={localImageUris}
          onPressLink={href => openLink(href)}
        />
      </ScrollView>
      <View
        style={[
          a.flex_row,
          a.align_center,
          a.justify_between,
          a.border_t,
          t.atoms.border_contrast_low,
          {paddingHorizontal: 18, paddingVertical: 12},
        ]}>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={_(msg`Back to edit`)}
          accessibilityHint=""
          onPress={() => {
            if (IS_WEB && previewHistoryPushedRef.current) {
              previewHistoryPushedRef.current = false
              window.history.back()
            }
            setPreviewSnapshot(undefined)
          }}>
          <Text
            style={[a.text_md, a.font_medium, {color: t.palette.primary_600}]}>
            <Trans>Back to edit</Trans>
          </Text>
        </Pressable>
        <Button
          label={editingArticle ? _(msg`Update`) : _(msg`Publish`)}
          variant="solid"
          color="primary"
          size="small"
          disabled={isPublishing}
          onPress={onPressConfirmPublish}>
          <ButtonText>
            {isPublishing ? (
              editingArticle ? (
                <Trans>Updating...</Trans>
              ) : (
                <Trans>Publishing...</Trans>
              )
            ) : editingArticle ? (
              <Trans>Update</Trans>
            ) : (
              <Trans>Publish</Trans>
            )}
          </ButtonText>
        </Button>
      </View>
    </View>
  )

  /*
   * The preview is a genuinely full-screen reading surface (matching the
   * public reader it shares `ArticleView` with), not a floating card over a
   * dark backdrop - so it deliberately bypasses `ComposerOverlay` entirely
   * rather than rendering through its `{previewSnapshot ? previewContent :
   * content}` desktop-modal branch. Still `Portal`-based, for the same
   * reason `ComposerOverlay` itself is: it needs to paint above the base
   * screen behind it regardless of that screen's own stacking context.
   * Rendered as a sibling overlay on top of `content` below, not as an
   * early return replacing it - `content` (and the real editor inside it)
   * stays mounted underneath the whole time; see the doc comment on the
   * final `return` for why that matters. The image-block menu is
   * intentionally left out here - it's only ever opened from inside the
   * editor's own WebView, which this overlay covers and blocks input to,
   * not something that needs its own preview-mode handling.
   */
  /*
   * Rendered identically (same position, same component) in both return
   * branches below, rather than only inside `content` - `doConfirmPublish`
   * fires from the preview branch and calls `.open()` on this control
   * right after clearing `previewSnapshot`, which swaps which branch
   * renders in the very same commit. If this prompt only existed in the
   * "normal" branch, that swap would be a fresh mount for it, and `.open()`
   * would race against `Portal`'s own effect-driven, two-commit mount (it
   * renders null and only pushes children into a shared Outlet via
   * `useEffect`+`setOutlet` - not synchronous, confirmed by reading
   * `components/Portal.tsx`). Kept mounted continuously across the swap
   * instead: React reconciles same-type siblings at the same position
   * without unmounting them, so this stays attached the whole time and
   * `.open()` never has to race anything.
   */
  const shareArticlePrompt = (
    <Prompt.Basic
      control={shareArticlePromptControl}
      title={_(msg`Your article has been published successfully!`)}
      description={_(msg`Would you like to share it to your timeline?`)}
      onConfirm={() => {
        if (!justPublished) return
        const {documentUri, title} = justPublished
        openComposer(
          async () => ({
            presetExternalLink: await resolveArticleShareLink(documentUri),
            text: title,
            logContext: 'ArticleShare',
          }),
          {},
        )
      }}
      confirmButtonCta={_(msg`Share`)}
      cancelButtonCta={_(msg`Not now`)}
      onClose={onClose}
    />
  )

  /*
   * `content` (and the real editor inside it) is rendered unconditionally
   * below, with preview as an overlay on top of it rather than a branch
   * swap that replaces it. This restores the contract `previewSnapshot`'s
   * own doc comment already states - "not persisted anywhere... not a
   * second source of truth", i.e. it assumes the editor is still there,
   * untouched, to return to. An earlier version of this returned early for
   * preview (and separately for a fresh publish), which unmounted `content`
   * outright: `useEditorBridge`'s `initialContent` is only ever read once,
   * at construction (see its own doc comment above), so a fresh remount
   * always came back seeded from `initial` - blank, for a new article -
   * silently discarding everything typed since. Reported directly by the
   * project owner: "click preview, click back to edit, the composer is
   * empty." Not a Release 2 regression - this early-return structure
   * predates it - but it's exactly what the fresh-publish branch's own
   * blank backdrop was quietly working around too, so that branch is
   * removed here as well: the editor now correctly still shows the
   * just-published content underneath the share prompt, rather than
   * needing to hide it.
   */
  return (
    <>
      <ComposerOverlay>
        {content}
        {imageMenuTarget ? (
          /*
           * Anchored over the block the author clicked - an image (Remove/
           * Replace) or a placeholder (Select image/Delete block), branching on
           * `imageMenuTarget.kind`. One mechanism serves both, since both
           * report the same shape of rect. The rect arrives from inside the
           * WebView, so it is in *that* document's viewport coordinates;
           * `onImageBlockMenuRef` above already converts it into page
           * coordinates via `editorSurfaceRef.measureInWindow` before it gets
           * here - confirmed live on web after an earlier version anchored near
           * the top-left of the screen instead of on the block.
           */
          <View
            style={[
              a.absolute,
              {
                left: imageMenuTarget.rect.x,
                top: imageMenuTarget.rect.y,
                width: imageMenuTarget.rect.width,
                height: imageMenuTarget.rect.height,
              },
            ]}
            pointerEvents="box-none">
            <Menu.Root control={imageMenuControl}>
              {/* Zero-affordance trigger: the real affordance is the block
                itself, inside the editor. This exists only to give the
                dropdown something to anchor to and the control something to
                open. */}
              <Menu.Trigger
                label={
                  imageMenuTarget.kind === 'image'
                    ? _(msg`Edit image`)
                    : _(msg`Image options`)
                }>
                {({props}) => (
                  /*
                   * The explicit `{null}` child is load-bearing. On web the
                   * trigger's `props` include Radix's own `children`, which is a
                   * *function* - spreading them onto an element with no children
                   * of its own hands React that function as a child and it
                   * throws "Functions are not valid as a React child". The
                   * cover-image menu in `Metadata.tsx` never hits this only
                   * because its trigger renders real content, which shadows the
                   * spread value.
                   */
                  <View {...props} style={[a.w_full, a.h_full]}>
                    {null}
                  </View>
                )}
              </Menu.Trigger>
              <Menu.Outer>
                {imageMenuTarget.kind === 'image' ? (
                  <>
                    <Menu.Item
                      label={_(msg`Remove image`)}
                      onPress={onRemoveBodyImage}>
                      <Menu.ItemIcon
                        icon={TrashIcon}
                        fill={imageMenuIconFill}
                      />
                      <Menu.ItemText>
                        <Trans>Remove image</Trans>
                      </Menu.ItemText>
                    </Menu.Item>
                    <Menu.Item
                      label={_(msg`Replace image`)}
                      onPress={() => {
                        void onReplaceBodyImage()
                      }}>
                      <Menu.ItemIcon
                        icon={AttachmentIcon}
                        fill={imageMenuIconFill}
                      />
                      <Menu.ItemText>
                        <Trans>Replace image</Trans>
                      </Menu.ItemText>
                    </Menu.Item>
                  </>
                ) : (
                  <>
                    {/*
                     * "Select image" covers both a retry after an error and
                     * picking for a block that is simply still idle - see
                     * `onSelectImageForPlaceholder`. "Delete block" removes the
                     * block outright, with no attempt to rejoin a paragraph its
                     * insert may have split; see `onDeleteImageBlock`.
                     */}
                    <Menu.Item
                      label={_(msg`Select image`)}
                      onPress={onSelectImageForPlaceholder}>
                      <Menu.ItemIcon
                        icon={AttachmentIcon}
                        fill={imageMenuIconFill}
                      />
                      <Menu.ItemText>
                        <Trans>Select image</Trans>
                      </Menu.ItemText>
                    </Menu.Item>
                    <Menu.Item
                      label={_(msg`Delete block`)}
                      onPress={onDeleteImageBlock}>
                      <Menu.ItemIcon
                        icon={TrashIcon}
                        fill={imageMenuIconFill}
                      />
                      <Menu.ItemText>
                        <Trans>Delete block</Trans>
                      </Menu.ItemText>
                    </Menu.Item>
                  </>
                )}
              </Menu.Outer>
            </Menu.Root>
          </View>
        ) : null}
      </ComposerOverlay>
      {previewSnapshot && previewDoc && (
        <Portal>
          <View style={[a.fixed, a.inset_0, t.atoms.bg]}>{previewContent}</View>
        </Portal>
      )}
      {shareArticlePrompt}
    </>
  )
}

/**
 * Shared full-bleed backdrop/Portal chrome for `ArticleCompose` - desktop
 * centered modal vs. mobile full-screen, per the confirmed Figma design.
 * Extracted so `ArticleEditLoadingGate` below can share the exact same
 * chrome instead of duplicating it, rather than rendering unstyled while an
 * article loads.
 */
function ComposerOverlay({
  children,
  onPressCancel,
}: {
  children: React.ReactNode
  onPressCancel?: () => void
}) {
  const {_} = useLingui()
  const t = useTheme()
  const {gtMobile} = useBreakpoints()

  if (gtMobile) {
    return (
      <Portal>
        <View
          style={[
            a.fixed,
            a.inset_0,
            // No explicit z-index, deliberately - this wrapper and any
            // Dialog.Outer/Prompt.Outer opened from within it (e.g.
            // DraftsListDialog) render as siblings through the same shared
            // Portal/Outlet. Dialog.Outer's own outer wrapper computes to an
            // *explicit* z-index:0 (not auto), which creates its own
            // stacking context - meaning ANY explicit z-index here, even a
            // small one, would always out-rank it regardless of DOM order,
            // trapping the dialog's real z-index (10) inside a losing
            // sibling subtree where it's never actually compared. Leaving
            // this at the implicit z-index:auto lets normal DOM order decide
            // instead: a dialog opened later mounts later in the DOM and
            // correctly wins, the same mechanism that already makes stacked
            // dialogs work everywhere else in the app. Still reliably paints
            // above the base app screen behind it - `position: fixed` alone
            // is enough for that, since the base screen isn't independently
            // positioned/z-indexed to begin with.
            a.align_center,
            a.justify_center,
            {backgroundColor: 'rgba(0,0,0,0.75)'},
            web({
              paddingTop: '50px' as unknown as number,
              paddingBottom: '50px' as unknown as number,
            }),
          ]}>
          {children}
        </View>
      </Portal>
    )
  }

  return (
    <Portal>
      <View
        style={[
          // `fixed`, not `absolute` (unlike this same file's own desktop
          // branch above, which already uses `a.fixed` successfully) - the
          // browser scrolls the page to keep a focused field visible above
          // the keyboard, and an `absolute` overlay (tied to a scrolling
          // ancestor) drifted with that scroll, opening a gap at the top;
          // `fixed` anchors to the viewport instead and is immune to it.
          // The mobile bottom toolbar's own keyboard-avoidance math lives
          // with that toolbar (see `toolbarLift` above `content`), not
          // here - this overlay itself needs no keyboard-awareness.
          a.fixed,
          a.inset_0,
          t.atoms.bg,
        ]}>
        {children}
        {onPressCancel && (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={_(msg`Cancel`)}
            accessibilityHint=""
            onPress={onPressCancel}
            style={[a.absolute, {top: 12, right: 12}, a.p_sm]}>
            <XIcon width={20} style={[t.atoms.text_contrast_medium]} />
          </Pressable>
        )}
      </View>
    </Portal>
  )
}

/**
 * `Stack.Screen` adapter - see the doc comment on `ArticleCompose` above.
 * Shared between `ArticleCompose` (create) and `ArticleEdit` (edit an
 * already-published article) - mirrors `StarterPackWizard`/`StarterPackEdit`
 * sharing `Wizard`. `rkey` is only ever present for the edit route; the
 * document URI it derives always uses the *current signed-in account's own*
 * DID, never anything route-suppliable, so this can never load another
 * account's article regardless of what `rkey` a deep link supplies.
 */
export function ArticleComposeScreen({
  route,
}: NativeStackScreenProps<
  CommonNavigatorParams,
  'ArticleCompose' | 'ArticleEdit'
>) {
  // `useNavigation<NavigationProp>()`, not the screen's own narrower
  // `navigation` prop - mirrors `Layout.Header.BackButton`'s `onPressBack`
  // exactly, which needs the app-wide route union (including `Home`) that
  // the stack-scoped prop type doesn't expose.
  const navigation = useNavigation<NavigationProp>()
  const {currentAccount} = useSession()
  const params = route.params ?? {}
  const rkey: string | undefined =
    'rkey' in params && typeof params.rkey === 'string'
      ? params.rkey
      : undefined
  const uri: AtUriString | undefined = rkey
    ? `at://${currentAccount!.did}/site.standard.document/${rkey}`
    : undefined
  const {
    data: initialArticle,
    isLoading,
    isError,
    refetch,
  } = useArticleDocumentQuery(uri)

  const onClose = () => {
    // `goBack()` silently no-ops if there's nothing in history to pop
    // (e.g. a direct URL load/refresh). Unlike `BackButton`'s fallback,
    // this uses `replace` rather than `navigate`: on a cold direct-URL
    // load, Home isn't in `state.routes` yet, so `navigate('Home')`
    // would *push* it on top instead of jumping to an existing entry -
    // leaving this route's own entry (and therefore its still-mounted,
    // still-visible component, per the LRU mount-caching in
    // `createNativeStackNavigatorWithAuth`) sitting right underneath.
    // `replace` removes this route from the stack outright, so it
    // actually unmounts instead of lingering as a phantom "back"
    // target for the next Cancel press.
    if (navigation.canGoBack()) {
      navigation.goBack()
    } else {
      navigation.replace('Home')
    }
  }

  if (rkey && (isLoading || (!initialArticle && !isError))) {
    return (
      <ArticleEditLoadingGate
        isError={false}
        onClose={onClose}
        onRetry={() => void refetch()}
      />
    )
  }
  if (rkey && (isError || !initialArticle)) {
    return (
      <ArticleEditLoadingGate
        isError
        onClose={onClose}
        onRetry={() => void refetch()}
      />
    )
  }

  return <ArticleCompose onClose={onClose} initialArticle={initialArticle} />
}

function ArticleEditLoadingGate({
  isError,
  onClose,
  onRetry,
}: {
  isError: boolean
  onClose: () => void
  onRetry: () => void
}) {
  const {_} = useLingui()
  const t = useTheme()
  return (
    <ComposerOverlay onPressCancel={onClose}>
      <View
        style={[
          a.flex_1,
          a.align_center,
          a.justify_center,
          t.atoms.bg,
          {gap: 16},
        ]}>
        {isError ? (
          <>
            <Text
              style={[a.text_md, a.text_center, t.atoms.text_contrast_medium]}>
              <Trans>Couldn't load this article.</Trans>
            </Text>
            <Button
              label={_(msg`Retry`)}
              variant="solid"
              color="primary"
              size="small"
              onPress={onRetry}>
              <ButtonText>
                <Trans>Retry</Trans>
              </ButtonText>
            </Button>
          </>
        ) : (
          <Loader size="xl" />
        )}
      </View>
    </ComposerOverlay>
  )
}
